import { downloadFile } from '@shared/utils'
import { cn } from 'lib/utils'
import { Pause, Play } from 'lucide-react'
import type React from 'react'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { AudioControls } from './controls'
import { SeekLine } from './seekline'

export type AudioPlayerHandle = HTMLDivElement & { seekToTime: (time: number) => void }

export interface AudioPlayerProps extends React.HTMLAttributes<HTMLDivElement> {
  audioUrl: string
  fileName?: string
}

export const PlayPauseIcon = ({
  playing,
  className,
  onClick,
  disabled,
}: {
  playing: boolean
  className?: string
  onClick?: (e: React.MouseEvent<SVGSVGElement>) => void
  disabled?: boolean
}) => (
  <div
    className={cn(
      'flex size-7 items-center justify-center rounded-md transition-colors',
      disabled ? 'bg-bg3 text-tertiary' : 'bg-brand text-brand-foreground hover:bg-brand/90',
      className,
    )}
  >
    {playing ? (
      <Pause size={11} className="cursor-pointer fill-current text-current" onClick={onClick} />
    ) : (
      <Play size={11} className="cursor-pointer fill-current text-current" onClick={onClick} />
    )}
  </div>
)

const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(
  ({ className, audioUrl, fileName = 'recording.mp3', ...props }, ref) => {
    const divRef = useRef<HTMLDivElement>(null)
    const audioRef = useRef<HTMLAudioElement>(null)
    const [playing, setPlaying] = useState(false)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [muted, setMuted] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [downloading, setDownloading] = useState(false)

    useImperativeHandle(
      ref,
      () => ({
        ...divRef.current!,
        seekToTime: (time: number) => {
          if (!audioRef.current) return
          if (!Number.isFinite(time)) return
          const max = Number.isFinite(audioRef.current.duration)
            ? audioRef.current.duration
            : Infinity
          const clamped = Math.max(0, Math.min(time, max))
          audioRef.current.currentTime = clamped
          audioRef.current.play().catch(() => {})
          setPlaying(true)
        },
      }),
      [],
    )

    const handlePlay = () => {
      if (!audioRef.current) return
      if (playing) audioRef.current.pause()
      else audioRef.current.play()
      setPlaying((prev) => !prev)
    }

    const handleRewind = () => {
      if (!audioRef.current) return
      audioRef.current.currentTime -= 10
      setCurrentTime(audioRef.current?.currentTime || 0)
    }

    const handleFastForward = () => {
      if (!audioRef.current) return
      audioRef.current.currentTime += 10
      setCurrentTime(audioRef.current?.currentTime || 0)
    }

    const handleMute = () => {
      if (!audioRef.current) return
      audioRef.current.muted = !audioRef.current.muted
      setMuted(audioRef.current.muted)
    }

    const handlePlaybackRate = (rate: number) => {
      if (!audioRef.current) return
      audioRef.current.playbackRate = rate
      setPlaybackRate(rate)
    }

    const formatTime = (time: number) => {
      if (time === 0) return '00:00'
      const roundedTime = Math.round(time)
      const minutes = Math.floor(roundedTime / 60)
      const seconds = Math.floor(roundedTime % 60)
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const resetPlayer = () => {
      setPlaying(false)
      setCurrentTime(0)
      if (audioRef.current) audioRef.current.currentTime = 0
    }

    const handleLoadedMetadata = () => {
      if (!audioRef.current) return
      if (Number.isNaN(audioRef.current.duration)) return
      setDuration(audioRef.current.duration)
    }

    const handleDownload = async () => {
      if (downloading) return
      setDownloading(true)
      try {
        await downloadFile(audioUrl, fileName)
      } finally {
        setDownloading(false)
      }
    }

    return (
      <div ref={divRef} className={cn('relative flex flex-col gap-3', className)} {...props}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AudioControls
            playing={playing}
            muted={muted}
            downloading={downloading}
            playbackRate={playbackRate}
            onPlay={handlePlay}
            onRewind={handleRewind}
            onForward={handleFastForward}
            onMute={handleMute}
            onDownload={handleDownload}
            onPlaybackRate={handlePlaybackRate}
          />
          <span className="ml-auto whitespace-nowrap font-jetbrains text-xs-400 text-secondary tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
        <SeekLine
          audioUrl={audioUrl}
          audioRef={audioRef}
          currentTime={currentTime}
          duration={duration}
          handleLoadedMetadata={handleLoadedMetadata}
          setCurrentTime={setCurrentTime}
          resetPlayer={resetPlayer}
        />
      </div>
    )
  },
)
AudioPlayer.displayName = 'AudioPlayer'

export { AudioPlayer }
